import React, { useState } from 'react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Key, Eye, EyeOff, ShieldCheck, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';

interface ForcePasswordChangeProps {
    user: any;
    onSuccess: () => void;
}

export const ForcePasswordChange: React.FC<ForcePasswordChangeProps> = ({ user, onSuccess }) => {
    const { login } = useAuth();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    // Password strength logic
    const getStrength = (pass: string) => {
        if (pass.length === 0) return { label: '', color: 'bg-slate-200', width: '0%', text: 'text-slate-400' };
        if (pass.length < 6) return { label: 'DÉBIL', color: 'bg-rose-500', width: '33%', text: 'text-rose-500' };
        if (pass.length < 10) return { label: 'BUENA', color: 'bg-amber-500', width: '66%', text: 'text-amber-500' };
        return { label: 'EXCELENTE', color: 'bg-emerald-500', width: '100%', text: 'text-emerald-500' };
    };

    const strength = getStrength(password);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (password.length < 6) {
            setError('La contraseña debe tener al menos 6 caracteres');
            return;
        }

        if (password !== confirmPassword) {
            setError('Las contraseñas no coinciden');
            return;
        }

        setLoading(true);
        try {
            const res = await api.updatePassword(user.id || user.email, password);
            if (res.success) {
                setSuccess(true);
                setTimeout(() => {
                    onSuccess();
                }, 1500);
            } else {
                setError(res.error || 'No se pudo actualizar la contraseña');
            }
        } catch (err) {
            setError('Error de conexión');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="flex flex-col items-center justify-center p-8 text-center animate-in fade-in zoom-in duration-500">
                <div className="w-24 h-24 bg-emerald-500 rounded-full flex items-center justify-center mb-6 shadow-2xl shadow-emerald-200">
                    <CheckCircle size={48} className="text-white animate-bounce" />
                </div>
                <h3 className="text-3xl font-black text-slate-800 uppercase italic tracking-tighter">¡Todo Listo!</h3>
                <p className="text-slate-400 font-bold text-sm uppercase mt-2 tracking-widest">Contraseña actualizada correctamente</p>
                <p className="text-indigo-500 font-black text-xs mt-6 animate-pulse uppercase tracking-[0.2em]">Redirigiendo...</p>
            </div>
        );
    }

    return (
        <div className="w-full max-w-md mx-auto animate-in slide-in-from-bottom-8 duration-700">
            <div className="bg-white rounded-[3.5rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.15)] overflow-hidden border border-slate-100 relative group">

                {/* Header Gradient */}
                <div className="bg-gradient-to-br from-[#FF6B00] via-[#FF8500] to-[#FF9D00] p-12 relative overflow-hidden text-center">
                    <div className="absolute inset-0 opacity-10 pointer-events-none">
                        <Key size={300} className="absolute -top-20 -left-20 rotate-12" />
                    </div>

                    <div className="relative z-10">
                        <div className="w-20 h-20 bg-white/20 backdrop-blur-xl rounded-[2rem] flex items-center justify-center mx-auto mb-6 border border-white/30 shadow-2xl animate-in zoom-in duration-700">
                            <Key size={32} className="text-white" />
                        </div>
                        <h2 className="text-3xl font-black text-white italic uppercase leading-none tracking-tighter mb-2">
                            Establece tu <br /> Contraseña
                        </h2>
                        <p className="text-white/80 text-[10px] font-black uppercase tracking-[0.3em] font-sans">
                            Acción requerida antes de continuar
                        </p>
                    </div>
                </div>

                <div className="p-10 space-y-8">
                    {/* Information Box */}
                    <div className="bg-amber-50/50 border border-amber-100 p-6 rounded-[2rem] flex gap-4 items-start">
                        <div className="mt-1 bg-amber-500/10 p-2 rounded-xl text-amber-600 shrink-0">
                            <ShieldCheck size={20} />
                        </div>
                        <p className="text-amber-800/80 font-bold text-xs leading-relaxed">
                            Tu cuenta fue creada con una clave temporal. Por seguridad, debes establecer una contraseña personal antes de poder usar el sistema.
                        </p>
                    </div>

                    {error && (
                        <div className="bg-rose-50 border border-rose-100 p-4 rounded-2xl flex items-center gap-3 text-rose-500 text-[10px] font-black uppercase tracking-wider animate-in shake duration-300">
                            <AlertTriangle size={16} /> {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* New Password */}
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Nueva Contraseña</label>
                            <div className="relative group">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full bg-slate-50 border-2 border-slate-100 rounded-[1.8rem] px-8 py-5 font-bold text-slate-700 outline-none focus:border-indigo-500 focus:bg-white transition-all duration-300 pr-16"
                                    placeholder="••••••••"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-300 hover:text-indigo-500 transition-colors"
                                >
                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>

                            {/* Strength Indicator */}
                            {password.length > 0 && (
                                <div className="px-4 space-y-1.5">
                                    <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full transition-all duration-500 ${strength.color}`}
                                            style={{ width: strength.width }}
                                        ></div>
                                    </div>
                                    <p className={`text-[9px] font-black uppercase tracking-widest ${strength.text}`}>
                                        {strength.label}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Confirm Password */}
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Confirmar Contraseña</label>
                            <div className="relative group">
                                <input
                                    type={showConfirm ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className={`w-full bg-slate-50 border-2 rounded-[1.8rem] px-8 py-5 font-bold text-slate-700 outline-none transition-all duration-300 pr-16 
                                        ${password && confirmPassword ? (password === confirmPassword ? 'border-emerald-500/50 bg-emerald-50/20' : 'border-rose-500/50 bg-rose-50/20') : 'border-slate-100 focus:border-indigo-500 focus:bg-white'}
                                    `}
                                    placeholder="••••••••"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirm(!showConfirm)}
                                    className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-300 hover:text-indigo-500 transition-colors"
                                >
                                    {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading || !password || password !== confirmPassword}
                            className="w-full relative overflow-hidden group/btn bg-gradient-to-r from-[#FF6B00] to-[#E66000] text-white py-6 rounded-[2rem] font-black uppercase text-xs tracking-[0.2em] shadow-2xl shadow-orange-500/30 transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:scale-100 disabled:shadow-none flex items-center justify-center gap-4 group"
                        >
                            <div className="absolute inset-0 bg-white/20 translate-y-full group-hover/btn:translate-y-0 transition-transform duration-300"></div>
                            {loading ? (
                                <Loader2 className="animate-spin" size={20} />
                            ) : (
                                <>
                                    <ShieldCheck size={20} className="group-hover:rotate-12 transition-transform" />
                                    <span>Guardar y Continuar</span>
                                </>
                            )}
                        </button>
                    </form>
                </div>
            </div>

            <p className="mt-8 text-center text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] opacity-50">
                Nexus Sentinel Security System v2.0
            </p>
        </div>
    );
};
